import * as React from "react";
import {
    Box,
    Card,
    CardContent,
    Container,
    Grid,
    Typography,
} from "@mui/material";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { constNull, flow, pipe } from "fp-ts/function";
import { array, either, option, task, taskEither } from "fp-ts";
import { pick, runTask } from "../../../utils/fp";
import { sequenceS } from "fp-ts/Apply";
import { supabase } from "../../../utils/supabase/init";
import { executeSupabaseQuery } from "../../../utils/supabase/useQuery";
import * as t from "io-ts";
import { newsFields } from "../../../domain/news";
import { Subdomain } from "../../../utils/subdomain/subdomain";
import {
    dateTimeFormat,
    LocalDate,
} from "../../../components/common/date/LocalDate";
import { MdxContent, validIsoDateStringCodec } from "../../../domain/common";
import { useTranslation } from "../../../utils/i18n/lang";
import { messages } from "../../../utils/i18n/i18n";
import { columns, join } from "../../../utils/pg-ts/pg-ts";
import { Mdx } from "../../../utils/mdx/Mdx";
import { serializeMdx } from "../../../utils/mdx/serializeMdx";
import {
    EventCard,
    EventCardFooter,
    EventCardLocationDate,
} from "../../../components/org/EventCard";
import { FejeLayout } from "../../../components/customOrgs/feje/FejeLayout";
import { NearestEvent, nearestEvents } from "../../../hooks/event";

const latestNewsFields = pipe(newsFields, pick("id", "content"), (fields) => ({
    ...fields,
    published: validIsoDateStringCodec, // TODO pg-ts untyped
}));
const latestNewsCodec = t.type(latestNewsFields);
type LatestNews = t.TypeOf<typeof latestNewsCodec>;

const latestNews = (orgSubdomain: Subdomain) =>
    pipe(
        executeSupabaseQuery(
            "latestNews",
            t.array(latestNewsCodec),
            (supabase) =>
                supabase
                    .from("news_published") // TODO pg-ts
                    .select(columns(latestNewsFields))
                    .eq("subdomain", orgSubdomain)
                    .range(0, 3)
                    .order("published", { ascending: false }),
            supabase
        ),
        taskEither.chainW(
            flow(
                array.map((news) =>
                    pipe(
                        news.content,
                        serializeMdx,
                        taskEither.map((mdxContent) => ({
                            ...news,
                            mdxContent,
                        }))
                    )
                ),
                taskEither.sequenceArray
            )
        )
    );

// TODO organization created_at is propagated to views... probaly not needed!, thi is an internal column
const subdomain = "feje" as Subdomain;

export const getServerSideProps = async (context: GetServerSidePropsContext) =>
    pipe(
        {
            subdomain: taskEither.right(subdomain), // TODO learn the fp way to add context
            latestNews: latestNews(subdomain),
            nearestEvents: nearestEvents(subdomain),
        },
        sequenceS(taskEither.ApplyPar),
        task.map(
            either.foldW(
                (e) => {
                    console.error(JSON.stringify(e));
                    return {
                        notFound: true as const, // as const have to be here :/
                    };
                },
                (props) => {
                    return {
                        props,
                    };
                }
            )
        ),
        runTask
    );

export default (p: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const m = useTranslation(msg);
    return (
        <FejeLayout subdomain={p.subdomain}>
            <Container maxWidth={"lg"}>
                <Grid container spacing={4} direction={"row-reverse"}>
                    <Grid
                        item
                        sm={4}
                        spacing={1}
                        container
                        direction={"column"}
                    >
                        <Grid item>
                            <Typography variant={"h5"} component={"div"}>
                                {m.news}
                            </Typography>
                        </Grid>
                        {p.latestNews.map((news) => (
                            <NewsCard key={news.id} {...news} />
                        ))}
                    </Grid>
                    <Grid
                        item
                        sm={8}
                        container
                        direction={"column"}
                        spacing={3}
                    >
                        {pipe(
                            p.nearestEvents,
                            array.head,
                            option.fold(constNull, (nearestEvent) => (
                                <Grid item>
                                    <EventCard
                                        image={nearestEvent.banner_image}
                                        paddingTop={"200px"}
                                        orgSubdomain={p.subdomain}
                                        eventId={nearestEvent.id}
                                    >
                                        <Typography variant={"h3"}>
                                            {nearestEvent.title}
                                        </Typography>

                                        <EventCardFooter>
                                            <EventCardLocationDate
                                                {...nearestEvent}
                                            />
                                        </EventCardFooter>
                                    </EventCard>
                                </Grid>
                            ))
                        )}

                        <Grid
                            item
                            container
                            direction={"row"}
                            spacing={2}
                            alignItems={"stretch"}
                            justifyContent={"flex-end"}
                        >
                            {pipe(
                                p.nearestEvents,
                                array.dropLeft(1),
                                array.map((event) => (
                                    <NextEventCard
                                        key={event.id}
                                        {...event}
                                        orgSubdomain={p.subdomain}
                                    />
                                ))
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </FejeLayout>
    );
};
interface NextEventCardProps extends NearestEvent {
    orgSubdomain: Subdomain;
}
const NextEventCard = (p: NextEventCardProps) => (
    <Grid item md={6} flexGrow={1}>
        <EventCard
            image={p.banner_image}
            paddingTop={"150px"}
            orgSubdomain={p.orgSubdomain}
            eventId={p.id}
        >
            <Typography variant={"h4"}>{p.title}</Typography>
            <EventCardLocationDate {...p} />
        </EventCard>
    </Grid>
);

interface NewsCardProps extends LatestNews {
    mdxContent: MdxContent;
}

const NewsCard = (p: NewsCardProps) => {
    return (
        <Grid item>
            <Card elevation={0}>
                <CardContent>
                    <Typography variant={"subtitle2"}>
                        <LocalDate date={p.published} format={dateTimeFormat} />
                    </Typography>
                    <Box sx={{ color: "text.secondary", typography: "body2" }}>
                        <Mdx mdxContent={p.mdxContent} />
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
};

const msg = messages(
    {
        news: "News",
    },
    {
        cs: {
            news: "Aktuality",
        },
    }
);
